import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Tag, Search, Filter, Loader2, Eye, QrCode, Package, Settings, Usb, FileText, X, Plus, Trash2, List, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

// Searchable Dropdown Component with Keyboard Navigation
const SearchableDropdown = ({ 
  label, 
  placeholder, 
  options, 
  value, 
  onChange, 
  disabled, 
  isLoading,
  displayKey = null,
  valueKey = null,
  testId,
  dropdownRef: externalRef
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const triggerRef = useRef(null);

  // Expose focus method via external ref
  useEffect(() => {
    if (externalRef) {
      externalRef.current = {
        focus: () => {
          if (triggerRef.current) {
            triggerRef.current.focus();
          }
        }
      };
    }
  }, [externalRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlighted index when options change or dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Find the index of currently selected value
      const selectedIdx = filteredOptions.findIndex(opt => getOptionValue(opt) === value);
      setHighlightedIndex(selectedIdx >= 0 ? selectedIdx : 0);
    }
  }, [isOpen]);

  // Reset highlighted index when search term changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(opt => {
      const displayValue = displayKey ? opt[displayKey] : opt;
      return displayValue?.toLowerCase().includes(term);
    });
  }, [options, searchTerm, displayKey]);

  const getDisplayValue = (opt) => displayKey ? opt[displayKey] : opt;
  const getOptionValue = (opt) => valueKey ? opt[valueKey] : opt;

  const selectedDisplay = useMemo(() => {
    if (!value) return '';
    const found = options.find(opt => getOptionValue(opt) === value);
    return found ? getDisplayValue(found) : value;
  }, [value, options, displayKey, valueKey]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
          // Scroll into view
          setTimeout(() => {
            const listEl = listRef.current;
            if (listEl) {
              const items = listEl.querySelectorAll('[data-option]');
              if (items[newIndex]) {
                items[newIndex].scrollIntoView({ block: 'nearest' });
              }
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : 0;
          // Scroll into view
          setTimeout(() => {
            const listEl = listRef.current;
            if (listEl) {
              const items = listEl.querySelectorAll('[data-option]');
              if (items[newIndex]) {
                items[newIndex].scrollIntoView({ block: 'nearest' });
              }
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          const selectedOpt = filteredOptions[highlightedIndex];
          onChange(getOptionValue(selectedOpt));
          setIsOpen(false);
          setSearchTerm('');
          setHighlightedIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="relative" ref={dropdownRef}>
        <div
          ref={triggerRef}
          className={`flex items-center h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'hover:border-gray-400'}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={!disabled ? handleKeyDown : undefined}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          data-testid={testId}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </div>
          ) : (
            <span className={selectedDisplay ? 'text-foreground' : 'text-muted-foreground'}>
              {selectedDisplay || placeholder}
            </span>
          )}
        </div>
        
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search..."
                  className="pl-8 h-9"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="max-h-48 overflow-y-auto" ref={listRef} role="listbox">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
              ) : (
                filteredOptions.map((opt, idx) => (
                  <div
                    key={idx}
                    data-option
                    role="option"
                    aria-selected={getOptionValue(opt) === value}
                    className={`px-3 py-2 text-sm cursor-pointer ${
                      idx === highlightedIndex 
                        ? 'bg-blue-100 text-blue-800' 
                        : getOptionValue(opt) === value 
                          ? 'bg-blue-50 text-blue-700' 
                          : 'hover:bg-gray-100'
                    }`}
                    onClick={() => {
                      onChange(getOptionValue(opt));
                      setIsOpen(false);
                      setSearchTerm('');
                      setHighlightedIndex(-1);
                    }}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                  >
                    {getDisplayValue(opt)}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Multi-Select Dropdown Component with "Select All" option and Keyboard Navigation
const MultiSelectDropdown = ({ 
  label, 
  placeholder, 
  options, 
  selectedValues = [], 
  onChange, 
  disabled, 
  isLoading,
  displayKey = null,
  valueKey = null,
  testId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1); // -1 = Select All, 0+ = options
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlighted index when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(-1); // Start at Select All
    }
  }, [isOpen]);

  // Reset highlighted index when search term changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(opt => {
      const displayValue = displayKey ? opt[displayKey] : opt;
      return displayValue?.toLowerCase().includes(term);
    });
  }, [options, searchTerm, displayKey]);

  const getDisplayValue = (opt) => displayKey ? opt[displayKey] : opt;
  const getOptionValue = (opt) => valueKey ? opt[valueKey] : opt;

  const isAllSelected = options.length > 0 && selectedValues.length === options.length;
  const isSomeSelected = selectedValues.length > 0 && selectedValues.length < options.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange([]); // Deselect all
    } else {
      onChange(options.map(opt => getOptionValue(opt))); // Select all
    }
  };

  const handleToggleOption = (optValue) => {
    if (selectedValues.includes(optValue)) {
      onChange(selectedValues.filter(v => v !== optValue));
    } else {
      onChange([...selectedValues, optValue]);
    }
  };

  const displayText = useMemo(() => {
    if (selectedValues.length === 0) return '';
    if (isAllSelected) return `All ${options.length} selected`;
    if (selectedValues.length === 1) {
      const found = options.find(opt => getOptionValue(opt) === selectedValues[0]);
      return found ? getDisplayValue(found) : selectedValues[0];
    }
    return `${selectedValues.length} selected`;
  }, [selectedValues, options, isAllSelected, displayKey, valueKey]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    const totalItems = filteredOptions.length + 1; // +1 for Select All

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
          // Scroll into view
          setTimeout(() => {
            const listEl = listRef.current;
            if (listEl && newIndex >= 0) {
              const items = listEl.querySelectorAll('[data-option]');
              if (items[newIndex]) {
                items[newIndex].scrollIntoView({ block: 'nearest' });
              }
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const newIndex = prev > -1 ? prev - 1 : -1;
          // Scroll into view
          setTimeout(() => {
            const listEl = listRef.current;
            if (listEl && newIndex >= 0) {
              const items = listEl.querySelectorAll('[data-option]');
              if (items[newIndex]) {
                items[newIndex].scrollIntoView({ block: 'nearest' });
              }
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightedIndex === -1) {
          // Select All
          handleSelectAll();
        } else if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          const selectedOpt = filteredOptions[highlightedIndex];
          handleToggleOption(getOptionValue(selectedOpt));
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="relative" ref={dropdownRef}>
        <div
          className={`flex items-center justify-between h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'hover:border-gray-400'}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={!disabled ? handleKeyDown : undefined}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          data-testid={testId}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </div>
          ) : (
            <>
              <span className={displayText ? 'text-foreground' : 'text-muted-foreground'}>
                {displayText || placeholder}
              </span>
              {selectedValues.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {selectedValues.length}
                </span>
              )}
            </>
          )}
        </div>
        
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-hidden">
            <div className="p-2 border-b sticky top-0 bg-white space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search..."
                  className="pl-8 h-9"
                  autoFocus
                />
              </div>
              {/* Select All option */}
              <div 
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border border-dashed border-gray-300 ${
                  highlightedIndex === -1 ? 'bg-blue-100 border-blue-300' : 'hover:bg-gray-100'
                }`}
                onClick={handleSelectAll}
                onMouseEnter={() => setHighlightedIndex(-1)}
              >
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={input => {
                    if (input) input.indeterminate = isSomeSelected;
                  }}
                  onChange={() => {}}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Select All ({options.length})
                </span>
              </div>
            </div>
            
            <div className="max-h-48 overflow-y-auto" ref={listRef} role="listbox">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
              ) : (
                filteredOptions.map((opt, idx) => {
                  const optValue = getOptionValue(opt);
                  const isSelected = selectedValues.includes(optValue);
                  const isHighlighted = idx === highlightedIndex;
                  return (
                    <div
                      key={idx}
                      data-option
                      role="option"
                      aria-selected={isSelected}
                      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                        isHighlighted 
                          ? 'bg-blue-100' 
                          : isSelected 
                            ? 'bg-blue-50' 
                            : 'hover:bg-gray-100'
                      }`}
                      onClick={() => handleToggleOption(optValue)}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className={isSelected ? 'text-blue-700 font-medium' : ''}>
                        {getDisplayValue(opt)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MainLabelPrinting = () => {
  const navigate = useNavigate();
  
  // Category filters (cascading dropdowns)
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  
  const [designs, setDesigns] = useState([]);
  const [shades, setShades] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [items, setItems] = useState([]);
  
  const [isDesignsLoading, setIsDesignsLoading] = useState(false);
  const [isShadesLoading, setIsShadesLoading] = useState(false);
  const [isSizesLoading, setIsSizesLoading] = useState(false);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  
  const [selectedDesign, setSelectedDesign] = useState('');
  // Multi-select for Shade, Size, Description
  const [selectedShades, setSelectedShades] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);  // Array of barcodes
  
  // Pricing and template selection
  const [pricingTypes, setPricingTypes] = useState([]);
  const [selectedPricingType, setSelectedPricingType] = useState('TN'); // Default to TN
  const [isPricingLoading, setIsPricingLoading] = useState(false);
  
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  
  // Print settings
  const [quantity, setQuantity] = useState(1);
  const [availableUsns, setAvailableUsns] = useState(0);
  
  // Batch Mode - Multiple items list
  const [batchItems, setBatchItems] = useState([]);

  // Import CSV (bulk barcode → batch list, one sticker per CSV row)
  const csvFileInputRef = useRef(null);
  const [csvImporting, setCsvImporting] = useState(false);

  const handleImportCsvClick = () => {
    if (csvFileInputRef.current) {
      csvFileInputRef.current.value = '';
      csvFileInputRef.current.click();
    }
  };

  // Parse CSV with optional 2nd-column sticker quantity.
  // 2026-05-28 — User wants to import `barcode, sticker_qty` per row
  // for bulk printing (e.g., 30 stickers for SKU A, 100 for SKU B in
  // a single CSV). Backwards-compatible with the legacy single-column
  // format: if a row has no qty (or qty <= 0), it defaults to 1.
  //
  // Accepted headers (case-insensitive):
  //   barcode
  //   barcode,quantity            (or qty / sticker_qty / stickers / count / no_of_stickers)
  //
  // Returns `[{barcode: string, qty: number}, ...]` preserving CSV
  // ordering. Duplicates are NOT deduped here — a `barcode,5` row
  // produces one batch entry with qty=5, NOT five separate entries.
  const _parseCsvBarcodes = (text) => {
    const rows = String(text || '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .map((r) => r.trim())
      .filter(Boolean);
    if (!rows.length) return [];

    const splitCols = (line) => line.split(',').map(
      (c) => c.trim().replace(/^"|"$/g, ''),
    );

    // Detect header — first column literally "barcode" (any case).
    const firstCols = splitCols(rows[0]);
    const headerIsLabel = /^barcode$/i.test(firstCols[0]);
    const QTY_HEADER = /^(qty|quantity|sticker[_\s-]?qty|stickers?|count|no[_\s-]?of[_\s-]?stickers?|labels?)$/i;
    const qtyColIdx = headerIsLabel
      ? firstCols.findIndex((c, i) => i > 0 && QTY_HEADER.test(c))
      : -1;
    const dataRows = headerIsLabel ? rows.slice(1) : rows;

    return dataRows
      .map((line) => {
        const cols = splitCols(line);
        const bc = cols[0];
        if (!bc) return null;
        let qty = 1;
        if (qtyColIdx > 0 && cols[qtyColIdx]) {
          const parsed = parseInt(cols[qtyColIdx], 10);
          if (Number.isFinite(parsed) && parsed > 0) qty = parsed;
        } else if (cols.length >= 2 && cols[1]) {
          // No qty header but a 2nd column is present — accept it as qty.
          const parsed = parseInt(cols[1], 10);
          if (Number.isFinite(parsed) && parsed > 0) qty = parsed;
        }
        return { barcode: bc, qty };
      })
      .filter(Boolean);
  };

  const handleImportCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvImporting(true);
    try {
      const text = await file.text();
      const parsed = _parseCsvBarcodes(text);
      if (!parsed.length) {
        toast.error('CSV is empty (need a `barcode` column; optional `quantity` column for sticker count).');
        return;
      }
      const unique = Array.from(new Set(parsed.map((p) => p.barcode)));
      const lookup = await apiClient.post(
        '/goods-transfer/item-master/lookup-by-barcodes',
        { barcodes: unique },
      );
      const itemsByBc = lookup.data?.items || {};
      const missing = lookup.data?.missing || [];

      const stamp = Date.now();
      const newRows = [];
      let appended = 0;
      let totalStickers = 0;
      let skipped = 0;
      parsed.forEach(({ barcode: bc, qty }, idx) => {
        const meta = itemsByBc[bc];
        if (!meta) {
          skipped++;
          return;
        }
        newRows.push({
          id: `csv-${stamp}-${idx}`,
          barcode: bc,
          designName: meta.design_name || '',
          shade: meta.shade || '',
          size: meta.size || '',
          pricingType: selectedPricingType || 'TN',
          template: selectedTemplate || '',
          boxTemplate: meta.box_template || '',
          quantity: qty,            // per-row sticker count (>= 1)
          source: 'csv_import',
        });
        appended++;
        totalStickers += qty;
      });

      if (!newRows.length) {
        toast.error(
          `No printable rows: ${missing.length} barcode${missing.length === 1 ? '' : 's'} not found in item master.`,
        );
        return;
      }
      setBatchItems((prev) => [...prev, ...newRows]);
      const msg =
        `Imported ${appended} barcode${appended === 1 ? '' : 's'} ` +
        `(${totalStickers.toLocaleString()} total sticker${totalStickers === 1 ? '' : 's'})` +
        (skipped > 0 ? ` · Skipped ${skipped} not found in item master` : '');
      toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'CSV import failed');
    } finally {
      setCsvImporting(false);
    }
  };
  
  // Preview
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  
  // Print Dialog
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printDialogTab, setPrintDialogTab] = useState('settings');
  const [printerLanguage, setPrinterLanguage] = useState('TSPL');
  const [printerModel, setPrinterModel] = useState('TSC_TE210'); // TSC_TE210 or TSC_MH240
  const [copiesPerLabel, setCopiesPerLabel] = useState(1);
  const [generatedCommands, setGeneratedCommands] = useState('');
  
  // Template selection in print dialog (for combo - different templates for inner vs box)
  const [dialogTemplate, setDialogTemplate] = useState('');
  
  // Pair Printing Mode (for combos - product + box labels)
  const [pairPrintingMode, setPairPrintingMode] = useState(false);
  const [labelType, setLabelType] = useState('product'); // 'product' or 'box'
  const [pendingPairBatches, setPendingPairBatches] = useState([]);
  const [selectedPairBatch, setSelectedPairBatch] = useState(null);
  const [lastPairBatchId, setLastPairBatchId] = useState(null);
  
  // Batch Combo Printing - tracks inner sticker batch IDs for box printing
  const [batchComboPairIds, setBatchComboPairIds] = useState([]); // Array of {barcode, pair_batch_id, quantity, pair_numbers}
  const [batchComboStep, setBatchComboStep] = useState('inner'); // 'inner' or 'box'
  const printingStepRef = useRef('inner'); // Track what step we're actually printing (persists through async)

  // Always use Arial Bold font (ARIALB.TTF)
  // Font sizes are now directly configured per-field in templates
  
  // Line spacing multiplier for consumer notice (loaded from admin settings)
  const [arialLineSpacingMultiplier, setArialLineSpacingMultiplier] = useState(2.5);

  // Printer configurations
  const PRINTER_CONFIGS = {
    TSC_TE210: {
      name: 'TSC TE210 (Desktop)',
      dpi: 203,
      speed: 4,
      density: 8,
      vendorId: 0x1203
    },
    TSC_MH240: {
      name: 'TSC MH240 (Industrial)',
      dpi: 203,  // MH240 is typically 203 DPI, but can be 300 DPI
      speed: 6,  // Industrial can go faster
      density: 10,
      vendorId: 0x1203
    }
  };

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
    fetchTemplates();
    fetchAvailableUsns();
    fetchPendingPairBatches();
    fetchPrintSettings();
  }, []);

  // Compute selected template data early for use in other hooks
  const selectedTemplateData = useMemo(() => {
    return templates.find(t => t.template_id === selectedTemplate);
  }, [templates, selectedTemplate]);
  
  const selectedTemplateType = selectedTemplateData?.template_type || '';

  // Fetch print settings from admin configuration (for line spacing only)
  const fetchPrintSettings = async () => {
    try {
      const response = await apiClient.get('/print-settings');
      if (response.data.settings) {
        const settings = response.data.settings;
        if (settings.arial_line_spacing_multiplier) {
          setArialLineSpacingMultiplier(settings.arial_line_spacing_multiplier);
        }
      }
    } catch (error) {
      console.error('Error fetching print settings:', error);
      // Keep defaults if fetch fails
    }
  };

  // Auto-enable pair printing for combo templates
  useEffect(() => {
    if (selectedTemplateType?.includes('combo')) {
      setPairPrintingMode(true);
    } else {
      setPairPrintingMode(false);
      setLabelType('product');
      setSelectedPairBatch(null);
    }
  }, [selectedTemplateType]);

  // Cascading dropdown effects
  useEffect(() => {
    if (selectedCategory) {
      fetchDesigns(selectedCategory);
      resetDownstream('category');
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedDesign) {
      fetchShades(selectedDesign);
      resetDownstream('design');
    }
  }, [selectedDesign]);

  // When shades are selected, fetch sizes for all selected shades
  useEffect(() => {
    if (selectedDesign && selectedShades.length > 0) {
      fetchSizesForMultipleShades(selectedDesign, selectedShades);
    } else {
      setSizes([]);
      setSelectedSizes([]);
    }
  }, [selectedShades]);

  // When sizes are selected, fetch items for all combinations
  useEffect(() => {
    if (selectedDesign && selectedShades.length > 0 && selectedSizes.length > 0) {
      fetchItemsForCombinations(selectedDesign, selectedShades, selectedSizes);
    } else {
      setItems([]);
      setSelectedItems([]);
    }
  }, [selectedSizes]);

  // Fetch pricing types when items are selected (use first item for reference)
  useEffect(() => {
    if (selectedItems.length > 0) {
      // Get pricing types from first selected item
      fetchPricingTypes(selectedItems[0]);
      setPreviewData(null);
    } else {
      setPricingTypes([]);
      setSelectedPricingType('');
    }
  }, [selectedItems]);

  const resetDownstream = (from) => {
    if (from === 'category') {
      setSelectedDesign('');
      setSelectedShades([]);
      setSelectedSizes([]);
      setSelectedItems([]);
      setShades([]);
      setSizes([]);
      setItems([]);
      setPricingTypes([]);
      setSelectedPricingType('');
      setPreviewData(null);
    } else if (from === 'design') {
      setSelectedShades([]);
      setSelectedSizes([]);
      setSelectedItems([]);
      setSizes([]);
      setItems([]);
      setPricingTypes([]);
      setSelectedPricingType('');
      setPreviewData(null);
    } else if (from === 'shade') {
      setSelectedSizes([]);
      setSelectedItems([]);
      setItems([]);
      setPricingTypes([]);
      setSelectedPricingType('');
      setPreviewData(null);
    } else if (from === 'size') {
      setSelectedItems([]);
      setPricingTypes([]);
      setSelectedPricingType('');
      setPreviewData(null);
    }
  };

  const fetchCategories = async () => {
    setIsCategoriesLoading(true);
    try {
      const response = await apiClient.get('/goods-transfer/item-master/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  const fetchDesigns = async (category) => {
    setIsDesignsLoading(true);
    try {
      const response = await apiClient.get(`/goods-transfer/item-master/designs?category4=${encodeURIComponent(category)}`);
      setDesigns(response.data.designs || []);
      
      // Auto-select if only one design exists for this category
      const designsData = response.data.designs || [];
      if (designsData.length === 1) {
        setSelectedDesign(designsData[0]);
      }
    } catch (error) {
      toast.error('Failed to load designs');
    } finally {
      setIsDesignsLoading(false);
    }
  };

  const fetchShades = async (design) => {
    setIsShadesLoading(true);
    try {
      const response = await apiClient.get(`/goods-transfer/item-master/shades?design_name=${encodeURIComponent(design)}`);
      const shadesData = response.data.shades || [];
      setShades(shadesData);
      
      // Auto-select if only one shade exists
      if (shadesData.length === 1) {
        setSelectedShades([shadesData[0]]);
      }
    } catch (error) {
      toast.error('Failed to load shades');
    } finally {
      setIsShadesLoading(false);
    }
  };

  // Fetch sizes for multiple selected shades - OPTIMIZED with single batch API call
  const fetchSizesForMultipleShades = async (design, shadesArray) => {
    setIsSizesLoading(true);
    try {
      // Use batch endpoint - single API call for all shades
      const shadesParam = shadesArray.join(',');
      const response = await apiClient.get(
        `/goods-transfer/item-master/sizes-batch?design_name=${encodeURIComponent(design)}&shades=${encodeURIComponent(shadesParam)}`
      );
      
      const sortedSizes = (response.data.sizes || []).sort();
      setSizes(sortedSizes);
      
      // Auto-select if only one size exists
      if (sortedSizes.length === 1) {
        setSelectedSizes([sortedSizes[0]]);
      }
    } catch (error) {
      toast.error('Failed to load sizes');
    } finally {
      setIsSizesLoading(false);
    }
  };

  // Fetch items for all combinations - OPTIMIZED with single batch API call
  const fetchItemsForCombinations = async (design, shadesArray, sizesArray) => {
    setIsItemsLoading(true);
    try {
      // Use batch endpoint - single API call for all shade/size combinations
      const shadesParam = shadesArray.join(',');
      const sizesParam = sizesArray.join(',');
      const response = await apiClient.get(
        `/goods-transfer/item-master/lookup-batch?design_name=${encodeURIComponent(design)}&shades=${encodeURIComponent(shadesParam)}&sizes=${encodeURIComponent(sizesParam)}`
      );
      
      const allItems = response.data.items || [];
      setItems(allItems);
      
      // Auto-select if only one item (description) exists
      if (allItems.length === 1) {
        setSelectedItems([allItems[0].barcode]);
      }
    } catch (error) {
      toast.error('Failed to load items');
    } finally {
      setIsItemsLoading(false);
    }
  };

  const fetchPricingTypes = async (barcode) => {
    setIsPricingLoading(true);
    try {
      const response = await apiClient.get(`/main-labels/pricing-types?barcode=${encodeURIComponent(barcode)}`);
      const pricingData = response.data.pricing_types || [];
      setPricingTypes(pricingData);
      // Auto-select TN if available, otherwise first option
      if (pricingData.includes('TN')) {
        setSelectedPricingType('TN');
      } else if (pricingData.length === 1) {
        setSelectedPricingType(pricingData[0]);
      } else if (pricingData.length > 0) {
        setSelectedPricingType(pricingData[0]);
      }
    } catch (error) {
      setPricingTypes([]);
    } finally {
      setIsPricingLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setIsTemplatesLoading(true);
    try {
      const response = await apiClient.get('/main-labels/templates');
      setTemplates(response.data.templates || []);
      // 2026-05-13 — DO NOT auto-select the default template. Operator
      // reported misalignment when the default template's roll size did
      // not match the physical label roll currently loaded in the printer.
      // The dropdown now starts blank so the operator must explicitly
      // pick a template every session, and a confirm dialog will surface
      // before each print run for an extra check.
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setIsTemplatesLoading(false);
    }
  };

  const fetchAvailableUsns = async () => {
    try {
      const response = await apiClient.get('/main-labels/available-usn-count');
      setAvailableUsns(response.data.available_count || 0);
    } catch (error) {
      console.error('Failed to fetch USN count:', error);
    }
  };

  const fetchPendingPairBatches = async () => {
    try {
      const response = await apiClient.get('/main-labels/pair-batches?status=pending_box');
      setPendingPairBatches(response.data.batches || []);
    } catch (error) {
      console.error('Failed to fetch pair batches:', error);
    }
  };

  const handlePreview = async () => {
    if (selectedItems.length === 0 || !selectedPricingType || !selectedTemplate) {
      toast.error('Please select items, pricing type, and template');
      return;
    }

    // Preview first selected item
    setIsPreviewLoading(true);
    try {
      const response = await apiClient.post('/main-labels/preview', {
        barcode: selectedItems[0],
        pricing_type: selectedPricingType,
        template_id: selectedTemplate
      });
      setPreviewData(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to generate preview';
      toast.error(msg);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Add all selected items (combinations) to batch list
  // Ref for design dropdown to focus after add to list
  const designDropdownRef = useRef(null);

  const handleAddToBatch = () => {
    if (selectedItems.length === 0 || !selectedPricingType || !selectedTemplate) {
      toast.error('Please select items, pricing type, and template');
      return;
    }
    if (quantity < 1) {
      toast.error('Please enter a valid quantity');
      return;
    }

    let addedCount = 0;
    let updatedCount = 0;
    const newBatchItems = [...batchItems];

    // Add each selected item (barcode) to batch
    for (const barcode of selectedItems) {
      const itemData = items.find(i => i.barcode === barcode);
      if (!itemData) continue;

      // Check if item already exists in batch with same pricing type
      const existingIndex = newBatchItems.findIndex(
        item => item.barcode === barcode && item.pricingType === selectedPricingType
      );

      if (existingIndex >= 0) {
        // Update quantity of existing item
        newBatchItems[existingIndex].quantity += quantity;
        updatedCount++;
      } else {
        // Add new item to batch
        const newItem = {
          id: Date.now() + Math.random(), // Ensure unique ID
          barcode: barcode,
          designName: itemData.design_name,
          shade: itemData.shade,
          size: itemData.size,
          pricingType: selectedPricingType,
          template: selectedTemplate,
          boxTemplate: itemData.box_template || '',  // Box sticker template for combo items
          quantity: quantity
        };
        newBatchItems.push(newItem);
        addedCount++;
      }
    }

    setBatchItems(newBatchItems);
    
    // Show summary message
    if (addedCount > 0 && updatedCount > 0) {
      toast.success(`Added ${addedCount} items, updated ${updatedCount} items (${quantity} labels each)`);
    } else if (addedCount > 0) {
      toast.success(`Added ${addedCount} items to print list (${quantity} labels each)`);
    } else if (updatedCount > 0) {
      toast.success(`Updated quantity for ${updatedCount} items`);
    }

    // Reset selection for next batch - BUT keep category intact
    // setSelectedCategory(''); // Keep category
    setSelectedDesign('');
    setSelectedShades([]);
    setSelectedSizes([]);
    setSelectedItems([]);
    // setDesigns([]); // Keep designs since category is kept
    setShades([]);
    setSizes([]);
    setItems([]);
    setPricingTypes([]);
    setSelectedPricingType('TN');
    setQuantity(1);
    setPreviewData(null);

    // Focus back to Design Name dropdown
    setTimeout(() => {
      if (designDropdownRef.current) {
        designDropdownRef.current.focus();
      }
    }, 100);
  };

  // Remove item from batch
  const handleRemoveFromBatch = (id) => {
    setBatchItems(batchItems.filter(item => item.id !== id));
    toast.success('Item removed from list');
  };

  // Clear entire batch
  const handleClearBatch = () => {
    setBatchItems([]);
    toast.success('List cleared');
  };

  // Calculate total labels in batch
  const totalBatchLabels = batchItems.reduce((sum, item) => sum + item.quantity, 0);

  const openPrintDialog = () => {
    setShowPrintDialog(true);
    setPrintDialogTab('settings');
    setGeneratedCommands('');
    // Initialize dialog template with the selected template (can be changed for combo inner/box)
    setDialogTemplate(selectedTemplate);
  };

  // FAST PATH (2026-06-11) — non-combo batch printing via /print-bulk.
  // The old flow fired ONE /main-labels/print call per CSV row (250-300
  // serial calls ≈ 45 min). Now rows are sent in chunks of 40 per request:
  // one USN claim + one bulk insert per chunk → seconds instead of minutes.
  const BULK_PRINT_CHUNK = 40;
  const _generateNonComboBatchCommands = async (printerConfig) => {
    let allCommands = '';
    let totalGenerated = 0;
    for (let i = 0; i < batchItems.length; i += BULK_PRINT_CHUNK) {
      const chunk = batchItems.slice(i, i + BULK_PRINT_CHUNK);
      console.log(`[Batch Print] Bulk chunk ${i / BULK_PRINT_CHUNK + 1}/${Math.ceil(batchItems.length / BULK_PRINT_CHUNK)} (${chunk.length} items)`);
      const response = await apiClient.post('/main-labels/print-bulk', {
        items: chunk.map((it) => ({
          barcode: it.barcode,
          pricing_type: it.pricingType,
          template_id: it.template,
          quantity: it.quantity * copiesPerLabel,
        })),
      });
      for (const res of (response.data.results || [])) {
        const labelData = res.label_data || {};
        const template = res.template;
        const templateType = template?.template_type || 'single';
        const mrpFormatted = res.mrp_formatted || `Rs.${labelData.mrp || 0}`;
        for (const usn of (res.assigned_usns || [])) {
          for (let copy = 0; copy < copiesPerLabel; copy++) {
            allCommands += generateTsplFromLabelData(labelData, template, usn, mrpFormatted, null, printerConfig, templateType);
          }
        }
        totalGenerated += res.quantity || 0;
      }
      (response.data.errors || []).forEach((er) => {
        toast.error(`${er.barcode}: ${er.error}`);
      });
    }
    return { allCommands, totalGenerated };
  };

  const generateCommands = async () => {
    const isBatchMode = batchItems.length > 0;
    
    if (!isBatchMode) {
      // Single item mode - original behavior
      if (selectedItems.length === 0 || !selectedPricingType || !selectedTemplate) {
        toast.error('Please complete all selections');
        return;
      }

      // For box labels in pair mode, need to select a pair batch
      if (pairPrintingMode && labelType === 'box' && !selectedPairBatch) {
        toast.error('Please select a pair batch for box labels');
        return;
      }
    }

    setIsPrinting(true);
    try {
      let allCommands = '';
      let totalGenerated = 0;
      const printerConfig = PRINTER_CONFIGS[printerModel];

      if (isBatchMode) {
        // Check if this is a combo template - requires inner + box printing
        const isComboTemplate = selectedTemplateType?.includes('combo');
        const currentStep = isComboTemplate ? batchComboStep : 'inner';
        
        // Use dialogTemplate for combo mode, otherwise use item's original template
        const templateToUse = isComboTemplate ? dialogTemplate : null;
        const templateData = templateToUse ? templates.find(t => t.template_id === templateToUse) : null;
        
        console.log(`[Batch Print] Starting batch processing for ${batchItems.length} items (combo: ${isComboTemplate}, step: ${currentStep}, template: ${templateToUse})`);
        
        // For combo box stickers, use the stored pair batch IDs
        if (isComboTemplate && currentStep === 'box') {
          console.log(`[Batch Print] Printing BOX stickers using ${batchComboPairIds.length} stored pair batches`);
          
          // Track pair number for each USN
          let globalPairIndex = 0;
          
          for (let i = 0; i < batchComboPairIds.length; i++) {
            const pairInfo = batchComboPairIds[i];
            const item = batchItems.find(b => b.barcode === pairInfo.barcode);
            if (!item) continue;
            
            console.log(`[Batch Print] Processing BOX for item ${i + 1}/${batchComboPairIds.length}: ${pairInfo.barcode}`);
            try {
              const printRequest = {
                barcode: pairInfo.barcode,
                pricing_type: item.pricingType,
                template_id: templateToUse || item.template, // Use dialog template for combo
                quantity: pairInfo.quantity * copiesPerLabel,
                pair_printing_mode: true,
                label_type: 'box',
                pair_batch_id: pairInfo.pair_batch_id
              };
              console.log(`[Batch Print] BOX API Request:`, printRequest);

              const response = await apiClient.post('/main-labels/print', printRequest);
              console.log(`[Batch Print] BOX API Response for item ${i + 1}:`, response.data?.quantity, 'labels');
              
              const labelData = response.data.label_data || {};
              const template = templateData || response.data.template;
              const templateType = template?.template_type || 'single';
              const mrpFormatted = response.data.mrp_formatted || `Rs.${labelData.mrp || 0}`;
              const usns = response.data.assigned_usns || [];
              const pairNumbers = pairInfo.pair_numbers || [];
              
              // Generate labels with pair numbers
              for (let usnIdx = 0; usnIdx < usns.length; usnIdx++) {
                const usn = usns[usnIdx];
                const pairNum = pairNumbers[usnIdx] || (globalPairIndex + usnIdx + 1);
                const usnWithPair = `${usn} (#${pairNum})`;
                for (let copy = 0; copy < copiesPerLabel; copy++) {
                  allCommands += generateTsplFromLabelData(labelData, template, usn, mrpFormatted, usnWithPair, printerConfig, templateType);
                }
              }
              globalPairIndex += usns.length;
              
              totalGenerated += response.data.quantity;
            } catch (itemError) {
              const errorMsg = itemError.response?.data?.detail || itemError.message || 'Unknown error';
              console.error(`[Batch Print] Error on BOX item ${i + 1}:`, errorMsg, itemError);
              toast.error(`Error printing BOX for item ${i + 1} (${item?.designName}): ${errorMsg}`);
              itemError._batchItemError = true;
              throw itemError;
            }
          }
          
          setGeneratedCommands(allCommands);
          setPrintDialogTab('commands');
          toast.success(`Generated ${totalGenerated} BOX sticker commands. Combo printing complete!`);
          
          // Clear everything after box stickers are printed
          setBatchItems([]);
          setBatchComboPairIds([]);
          setBatchComboStep('inner');
          fetchPendingPairBatches();
        } else if (!isComboTemplate) {
          // FAST PATH — bulk endpoint, chunks of 40 rows per request
          const { allCommands: bulkCommands, totalGenerated: bulkTotal } =
            await _generateNonComboBatchCommands(printerConfig);
          allCommands = bulkCommands;
          totalGenerated = bulkTotal;
          setGeneratedCommands(allCommands);
          setPrintDialogTab('commands');
          toast.success(`Generated commands for ${totalGenerated} labels (${batchItems.length} items)`);
          setBatchItems([]);
        } else {
          // Print INNER stickers (or regular non-combo stickers)
          const collectedPairIds = []; // Collect pair batch IDs for combo templates
          
          // Track pair number for combo printing
          let globalPairIndex = 0;
          
          for (let i = 0; i < batchItems.length; i++) {
            const item = batchItems[i];
            console.log(`[Batch Print] Processing item ${i + 1}/${batchItems.length}: ${item.barcode} (${item.designName})`);
            try {
              const printRequest = {
                barcode: item.barcode,
                pricing_type: item.pricingType,
                template_id: templateToUse || item.template, // Use dialog template for combo
                quantity: item.quantity * copiesPerLabel,
                pair_printing_mode: isComboTemplate,
                label_type: 'product'
              };
              console.log(`[Batch Print] API Request:`, printRequest);

              const response = await apiClient.post('/main-labels/print', printRequest);
              console.log(`[Batch Print] API Response for item ${i + 1}:`, response.data?.quantity, 'USNs assigned');
              
              // Store pair batch ID and pair numbers for combo templates
              if (isComboTemplate && response.data.pair_batch_id) {
                collectedPairIds.push({
                  barcode: item.barcode,
                  pair_batch_id: response.data.pair_batch_id,
                  quantity: response.data.quantity,
                  pair_numbers: response.data.pair_numbers || []
                });
              }
              
              // Generate TSPL commands for TSC printers (.prn format)
              const labelData = response.data.label_data || {};
              const template = templateData || response.data.template;
              const templateType = template?.template_type || 'single';
              const mrpFormatted = response.data.mrp_formatted || `Rs.${labelData.mrp || 0}`;
              const usns = response.data.assigned_usns || [];
              const pairNumbers = response.data.pair_numbers || [];
              
              console.log(`[Batch Print] Generating TSPL for ${usns.length} USNs`);
              for (let usnIdx = 0; usnIdx < usns.length; usnIdx++) {
                const usn = usns[usnIdx];
                // For combo templates, add pair number beside USN
                const pairNum = isComboTemplate ? (pairNumbers[usnIdx] || (globalPairIndex + usnIdx + 1)) : null;
                const usnWithPair = isComboTemplate ? `${usn} (#${pairNum})` : null;
                for (let copy = 0; copy < copiesPerLabel; copy++) {
                  allCommands += generateTsplFromLabelData(labelData, template, usn, mrpFormatted, usnWithPair, printerConfig, templateType);
                }
              }
              if (isComboTemplate) {
                globalPairIndex += usns.length;
              }
              
              totalGenerated += response.data.quantity;
              console.log(`[Batch Print] Item ${i + 1} complete. Total generated so far: ${totalGenerated}`);
            } catch (itemError) {
              const errorMsg = itemError.response?.data?.detail || itemError.message || 'Unknown error';
              console.error(`[Batch Print] Error on item ${i + 1}:`, errorMsg, itemError);
              toast.error(`Error printing item ${i + 1} (${item.designName}): ${errorMsg}`);
              // Mark error as batch item error to avoid duplicate toast
              itemError._batchItemError = true;
              throw itemError; // Re-throw to stop batch processing
            }
          }

          setGeneratedCommands(allCommands);
          setPrintDialogTab('commands');
          
          if (isComboTemplate) {
            // Store pair IDs and switch to box step - DO NOT clear batch
            setBatchComboPairIds(collectedPairIds);
            setBatchComboStep('box');
            toast.success(`Generated ${totalGenerated} INNER sticker commands. Now click "Generate Commands" again to print BOX stickers.`);
            // Refresh pair batches
            fetchPendingPairBatches();
          } else {
            toast.success(`Generated commands for ${totalGenerated} labels (${batchItems.length} items)`);
            // Clear batch after successful generation (non-combo only)
            setBatchItems([]);
          }
        }
      } else {
        // Single item mode
        const printRequest = {
          barcode: selectedItems[0],
          pricing_type: selectedPricingType,
          template_id: selectedTemplate,
          quantity: quantity * copiesPerLabel,
          pair_printing_mode: pairPrintingMode,
          label_type: labelType
        };

        // Add pair_batch_id for box labels
        if (pairPrintingMode && labelType === 'box' && selectedPairBatch) {
          printRequest.pair_batch_id = selectedPairBatch.pair_batch_id;
        }

        const response = await apiClient.post('/main-labels/print', printRequest);

        // Generate TSPL commands for TSC printers (.prn format)
        const printerConfig = PRINTER_CONFIGS[printerModel];
        const labelData = response.data.label_data || {};
        const template = response.data.template;
        const mrpFormatted = response.data.mrp_formatted || `Rs.${labelData.mrp || 0}`;
        const usns = response.data.assigned_usns || [];
        
        let commands = '';
        for (const usn of usns) {
          for (let copy = 0; copy < copiesPerLabel; copy++) {
            commands += generateTsplFromLabelData(labelData, template, usn, mrpFormatted, null, printerConfig, selectedTemplateType);
          }
        }
        
        setGeneratedCommands(commands);
        setPrintDialogTab('commands');
        
        // Show success message with pair info
        if (pairPrintingMode) {
          const pairRange = response.data.pair_numbers;
          if (labelType === 'product') {
            const newPairBatchId = response.data.pair_batch_id;
            setLastPairBatchId(newPairBatchId);
            toast.success(`Generated ${response.data.quantity} INNER labels (Pairs #${pairRange[0]} - #${pairRange[pairRange.length - 1]}). Now print BOX labels.`);
            
            // Auto-switch to box label mode and pre-select the just-created batch
            // Refresh pair batches first, then auto-select
            const batchesResponse = await apiClient.get('/main-labels/pair-batches?status=pending_box');
            const batches = batchesResponse.data.batches || [];
            setPendingPairBatches(batches);
            
            // Find and select the just-created batch
            const newBatch = batches.find(b => b.pair_batch_id === newPairBatchId);
            if (newBatch) {
              setLabelType('box');
              setSelectedPairBatch(newBatch);
              toast.info('Switched to BOX label mode. Print again to get matching box labels with same USNs.');
            }
          } else {
            toast.success(`Generated ${response.data.quantity} BOX labels with matching USNs`);
            // Reset to product mode for next combo
            setLabelType('product');
            setSelectedPairBatch(null);
          }
          // Refresh pair batches
          fetchPendingPairBatches();
        } else {
          toast.success(`Generated commands for ${response.data.quantity} labels`);
        }
      }
      
      // Refresh USN count
      fetchAvailableUsns();
      
    } catch (error) {
      // Enhanced error logging for debugging
      console.error('Print error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      // The specific item error was already shown in the batch loop (if batch mode)
      // Only show generic error for single item mode or unexpected errors
      const msg = error.response?.data?.detail || error.message || 'Failed to generate commands';
      if (!error._batchItemError) {
        toast.error(msg);
      }
    } finally {
      setIsPrinting(false);
    }
  };

  const generateTsplFromLabelData = (labelData, template, usnCode, mrpFormatted, displayUsn = null, printerConfig = null, templateType = null) => {
    // Generate native TSPL commands for TSC printers
    // Uses template field positions from database configuration
    const config = printerConfig || PRINTER_CONFIGS.TSC_TE210;
    const widthMm = template ? Math.round(template.label_width_dots / 8) : 100;
    const heightMm = template ? Math.round(template.label_height_dots / 8) : 80;
    const usnDisplay = displayUsn || usnCode;
    const fields = template?.fields || {};
    const tplType = templateType || template?.template_type || '';
    
    // Helper to get field position with defaults
    const getField = (key, defaultX = 0, defaultY = 0, defaultFontSize = 2, defaultSize = 4) => {
      const field = fields[key] || {};
      return {
        x: field.x ?? defaultX,
        y: field.y ?? defaultY,
        font_size: field.font_size ?? defaultFontSize,
        bold: field.bold ?? false,
        size: field.size ?? defaultSize,
        text: field.text || ''
      };
    };
    
    // Generate TEXT command using Arial Bold TrueType font
    // Font size is directly from template (decimal value like 10.5, 14.0, etc.)
    const generateTextCmd = (x, y, fontSize, text) => {
      // Use ARIALB.TTF TrueType font with direct size from template
      const arialSize = fontSize || 10.5;
      // TSPL TrueType syntax: TEXT x,y,"font",rotation,x-mul,y-mul,"content"
      return `TEXT ${x},${y},"ARIALB.TTF",0,${arialSize},${arialSize},"${text}"\r\n`;
    };
    
    let tspl = '';
    
    // Printer initialization with config-specific settings
    tspl += `SIZE ${widthMm} mm, ${heightMm} mm\r\n`;
    tspl += `GAP 3 mm, 0 mm\r\n`;
    tspl += `SPEED ${config.speed}\r\n`;
    tspl += `DENSITY ${config.density}\r\n`;
    tspl += `DIRECTION 1,0\r\n`;
    tspl += `REFERENCE 0,0\r\n`;
    tspl += `OFFSET 0 mm\r\n`;
    tspl += `SET PEEL OFF\r\n`;
    tspl += `SET CUTTER OFF\r\n`;
    tspl += `SET PARTIAL_CUTTER OFF\r\n`;
    tspl += `CLS\r\n`;
    
    // QUALITY Label & Value
    const qualityLabel = getField('quality_label', 16, 16, 10.5);
    const qualityValue = getField('quality_value', 160, 16, 14.0);
    if (labelData.quality) {
      tspl += generateTextCmd(qualityLabel.x, qualityLabel.y, qualityLabel.font_size, qualityLabel.text || 'QUALITY');
      tspl += generateTextCmd(qualityValue.x, qualityValue.y, qualityValue.font_size, labelData.quality);
    }
    
    // CONTENT Label & Value - Display content items with spaces (not "&")
    const contentLabel = getField('content_label', 16, 56, 10.5);
    const contentValue = getField('content_value', 160, 56, 14.0);
    if (labelData.content1) {
      // Build content string with spaces between items (no "&" separator)
      let contentParts = [labelData.content1];
      if (labelData.content2) contentParts.push(labelData.content2);
      if (labelData.content3) contentParts.push(labelData.content3);
      const content = contentParts.join('    ');  // Use spaces as separator
      tspl += generateTextCmd(contentLabel.x, contentLabel.y, contentLabel.font_size, contentLabel.text || 'CONTENT');
      tspl += generateTextCmd(contentValue.x, contentValue.y, contentValue.font_size, content);
    }
    
    // SIZE Label & Value - Display size values from Desc2, Desc4, Desc7 with spaces
    const sizeLabel = getField('size_label', 16, 96, 10.5);
    const sizeValue = getField('size_value', 160, 96, 10.5);
    // Build size string from size1 (desc2), size2 (desc4), size3 (desc7)
    let sizeParts = [];
    if (labelData.size1) sizeParts.push(labelData.size1);
    if (labelData.size2) sizeParts.push(labelData.size2);
    if (labelData.size3) sizeParts.push(labelData.size3);
    const size = sizeParts.length > 0 ? sizeParts.join('    ') : (labelData.size_from_rates || '');
    if (size) {
      tspl += generateTextCmd(sizeLabel.x, sizeLabel.y, sizeLabel.font_size, sizeLabel.text || 'SIZE');
      tspl += generateTextCmd(sizeValue.x, sizeValue.y, sizeValue.font_size, size);
    }
    
    // DESC5 Bold - for combo stickers (typically displayed prominently, e.g., "36 F/S")
    if (labelData.desc5_bold && tplType?.includes('combo')) {
      const desc5Field = getField('desc5_bold', 400, 56, 14.0);
      // For bold text, we use a slightly larger font or repeat the text
      tspl += generateTextCmd(desc5Field.x, desc5Field.y, desc5Field.font_size, labelData.desc5_bold);
    }
    
    // COLOR Label & Value (if present)
    if (labelData.color) {
      const colorLabel = getField('color_label', 16, 136, 10.5);
      const colorValue = getField('color_value', 160, 136, 10.5);
      tspl += generateTextCmd(colorLabel.x, colorLabel.y, colorLabel.font_size, colorLabel.text || 'COLOR');
      tspl += generateTextCmd(colorValue.x, colorValue.y, colorValue.font_size, labelData.color);
    }
    
    // QTY (value configurable via template — falls back to label data / default)
    const qtyField = getField('qty', 16, 176, 10.5);
    const qtyVal = template?.fields?.qty?.value;
    const qtyText = (qtyVal !== undefined && qtyVal !== null && qtyVal !== '')
      ? `QTY : ${qtyVal} N`
      : (labelData.qty || 'QTY : 1 N');
    tspl += generateTextCmd(qtyField.x, qtyField.y, qtyField.font_size, qtyText);
    
    // MRP Label & Value
    const mrpLabel = getField('mrp_label', 200, 176, 10.5);
    const mrpValue = getField('mrp_value', 280, 166, 18.0);
    tspl += generateTextCmd(mrpLabel.x, mrpLabel.y, mrpLabel.font_size, mrpLabel.text || 'MRP:');
    tspl += generateTextCmd(mrpValue.x, mrpValue.y, mrpValue.font_size, mrpFormatted);

    // WSP (wholesale price) — renders only when the template positions the WSP
    // field AND the rate carries a WSP value. Keeps existing labels untouched.
    const wspValue = getField('wsp_value', 0, 0, 18.0);
    const wspText = labelData.wsp_formatted
      || (labelData.wsp ? `Rs.${Number(labelData.wsp).toFixed(2)}` : '');
    if ((wspValue.x || wspValue.y) && wspText) {
      const wspLabel = getField('wsp_label', 0, 0, 10.5);
      if (wspLabel.x || wspLabel.y) {
        tspl += generateTextCmd(wspLabel.x, wspLabel.y, wspLabel.font_size, wspLabel.text || 'WSP :');
      }
      tspl += generateTextCmd(wspValue.x, wspValue.y, wspValue.font_size, wspText);
    }
    
    // Tax Notice
    const taxNotice = getField('tax_notice', 280, 210, 7.5);
    tspl += generateTextCmd(taxNotice.x, taxNotice.y, taxNotice.font_size, '(INCL. OF ALL TAXES)');
    
    // MADE IN INDIA
    const madeInIndia = getField('made_in_india', 16, 250, 10.5);
    tspl += generateTextCmd(madeInIndia.x, madeInIndia.y, madeInIndia.font_size, 'MADE IN INDIA');
    
    // QR Code 1 (USN)
    const qr1 = getField('qr1', 16, 290, 10.5, 5);
    const qr1Text = getField('qr1_text', 16, 410, 10.5);
    if (usnCode) {
      tspl += `QRCODE ${qr1.x},${qr1.y},L,${qr1.size},A,0,"${usnCode}"\r\n`;
      tspl += generateTextCmd(qr1Text.x, qr1Text.y, qr1Text.font_size, usnDisplay);
    }
    
    // QR Code 2 (Optional Barcode)
    const qr2 = getField('qr2', 200, 290, 10.5, 5);
    const qr2Text = getField('qr2_text', 200, 410, 10.5);
    if (labelData.qr2_code) {
      tspl += `QRCODE ${qr2.x},${qr2.y},L,${qr2.size},A,0,"${labelData.qr2_code}"\r\n`;
      tspl += generateTextCmd(qr2Text.x, qr2Text.y, qr2Text.font_size, labelData.qr2_code);
    }
    
    // Consumer Notice - plain text with "Note:" prefix (no box)
    const consumerNotice = getField('consumer_notice', 16, 460, 7.5);
    // Line height calculated from actual font size with configurable spacing multiplier
    const actualFontSize = consumerNotice.font_size || 7.5;
    const lineHeight = Math.ceil(actualFontSize * arialLineSpacingMultiplier);
    
    tspl += generateTextCmd(consumerNotice.x, consumerNotice.y, consumerNotice.font_size, 'Note: Consumer is free to open and');
    tspl += generateTextCmd(consumerNotice.x, consumerNotice.y + lineHeight, consumerNotice.font_size, 'inspect the product before buying it');
    
    // Print command
    tspl += `PRINT 1,1\r\n`;
    
    return tspl;
  };

  const convertZplToTspl = (zplCommands, template) => {
    // Convert ZPL to TSPL format for TSC printers
    const widthMm = template ? Math.round(template.label_width_dots / 8) : 100;
    const heightMm = template ? Math.round(template.label_height_dots / 8) : 80;
    
    let tspl = '';
    const labels = zplCommands.split('^XZ');
    
    labels.forEach((label, idx) => {
      if (!label.trim()) return;
      
      // Printer initialization with proper line endings for TSC
      tspl += `SIZE ${widthMm} mm, ${heightMm} mm\r\n`;
      tspl += `GAP 3 mm, 0 mm\r\n`;
      tspl += `SPEED 4\r\n`;
      tspl += `DENSITY 8\r\n`;
      tspl += `DIRECTION 1,0\r\n`;
      tspl += `REFERENCE 0,0\r\n`;
      tspl += `CLS\r\n`;
      
      // Extract text fields from ZPL and convert
      const textMatches = label.matchAll(/\^FO(\d+),(\d+)\^A(\w+),(\d+),(\d+)\^FD([^^]*)\^FS/g);
      for (const match of textMatches) {
        const x = Math.round(parseInt(match[1]) / 8);
        const y = Math.round(parseInt(match[2]) / 8);
        const fontHeight = parseInt(match[4]);
        const text = match[6];
        if (text) {
          // Map font size: ZPL font height to TSPL font number
          const tsplFont = fontHeight > 50 ? "4" : fontHeight > 30 ? "3" : "2";
          tspl += `TEXT ${x},${y},"${tsplFont}",0,1,1,"${text}"\r\n`;
        }
      }
      
      // Extract QR codes
      const qrMatches = label.matchAll(/\^FO(\d+),(\d+)\^BQN,2,(\d+)\^FDQA,([^^]*)\^FS/g);
      for (const match of qrMatches) {
        const x = Math.round(parseInt(match[1]) / 8);
        const y = Math.round(parseInt(match[2]) / 8);
        const size = parseInt(match[3]);
        const data = match[4];
        if (data) {
          // TSPL QRCODE: X,Y,ECC Level,Cell width,Mode,Rotation,"Data"
          tspl += `QRCODE ${x},${y},L,${size},A,0,"${data}"\r\n`;
        }
      }
      
      // Extract box commands
      const boxMatches = label.matchAll(/\^FO(\d+),(\d+)\^GB(\d+),(\d+),(\d+)\^FS/g);
      for (const match of boxMatches) {
        const x = Math.round(parseInt(match[1]) / 8);
        const y = Math.round(parseInt(match[2]) / 8);
        const width = Math.round(parseInt(match[3]) / 8);
        const height = Math.round(parseInt(match[4]) / 8);
        const thickness = parseInt(match[5]);
        tspl += `BOX ${x},${y},${x + width},${y + height},${thickness}\r\n`;
      }
      
      tspl += `PRINT 1,1\r\n\r\n`;
    });
    
    return tspl;
  };

  const handlePrintViaUsb = async () => {
    let commandsToSend = generatedCommands;
    const printerConfig = PRINTER_CONFIGS[printerModel];
    
    // Check if WebUSB is supported
    if (!navigator.usb) {
      toast.error('USB printing is not supported on this device/browser. Please use a desktop browser with WebUSB support (Chrome, Edge, or Opera).');
      return;
    }
    
    // Generate commands if not already generated
    if (!commandsToSend) {
      const isBatchMode = batchItems.length > 0;
      
      if (isBatchMode) {
        // For batch mode, validate required fields based on batch items
        if (batchItems.length === 0) {
          toast.error('No items in batch to print');
          return;
        }
        
        setIsPrinting(true);
        try {
          // Generate commands inline for batch mode
          let allCommands = '';
          let totalGenerated = 0;
          
          // Check if this is a combo template
          const isComboTemplate = selectedTemplateType?.includes('combo');
          const currentStep = isComboTemplate ? batchComboStep : 'inner';
          const templateToUse = isComboTemplate ? dialogTemplate : null;
          const templateData = templateToUse ? templates.find(t => t.template_id === templateToUse) : null;
          
          // Track what step we're printing (ref persists through async operations)
          printingStepRef.current = currentStep;
          
          console.log(`[USB Print] Starting batch processing for ${batchItems.length} items (combo: ${isComboTemplate}, step: ${currentStep})`);
          
          // For combo box stickers, use the stored pair batch IDs
          if (isComboTemplate && currentStep === 'box') {
            // BOX printing logic
            let globalPairIndex = 0;
            
            for (let i = 0; i < batchComboPairIds.length; i++) {
              const pairInfo = batchComboPairIds[i];
              const item = batchItems.find(b => b.barcode === pairInfo.barcode);
              if (!item) continue;
              
              const printRequest = {
                barcode: pairInfo.barcode,
                pricing_type: item.pricingType,
                template_id: templateToUse || item.template,
                quantity: pairInfo.quantity * copiesPerLabel,
                pair_printing_mode: true,
                label_type: 'box',
                pair_batch_id: pairInfo.pair_batch_id
              };

              const response = await apiClient.post('/main-labels/print', printRequest);
              
              const labelData = response.data.label_data || {};
              const template = templateData || response.data.template;
              const templateType = template?.template_type || 'single';
              const mrpFormatted = response.data.mrp_formatted || `Rs.${labelData.mrp || 0}`;
              const usns = response.data.assigned_usns || [];
              const pairNumbers = pairInfo.pair_numbers || [];
              
              for (let usnIdx = 0; usnIdx < usns.length; usnIdx++) {
                const usn = usns[usnIdx];
                const pairNum = pairNumbers[usnIdx] || (globalPairIndex + usnIdx + 1);
                const usnWithPair = `${usn} (#${pairNum})`;
                for (let copy = 0; copy < copiesPerLabel; copy++) {
                  allCommands += generateTsplFromLabelData(labelData, template, usn, mrpFormatted, usnWithPair, printerConfig, templateType);
                }
              }
              globalPairIndex += usns.length;
              totalGenerated += response.data.quantity;
            }
          } else if (!isComboTemplate) {
            // FAST PATH — bulk endpoint, chunks of 40 rows per request
            const { allCommands: bulkCommands, totalGenerated: bulkTotal } =
              await _generateNonComboBatchCommands(printerConfig);
            allCommands = bulkCommands;
            totalGenerated = bulkTotal;
          } else {
            // INNER printing logic (or non-combo)
            const newPairBatchIds = [];
            let globalPairIndex = 0;
            
            for (let i = 0; i < batchItems.length; i++) {
              const item = batchItems[i];
              const printRequest = {
                barcode: item.barcode,
                pricing_type: item.pricingType,
                template_id: templateToUse || item.template,
                quantity: item.quantity * copiesPerLabel,
                pair_printing_mode: isComboTemplate,
                label_type: 'product'
              };

              const response = await apiClient.post('/main-labels/print', printRequest);
              
              const labelData = response.data.label_data || {};
              const template = templateData || response.data.template;
              const templateType = template?.template_type || 'single';
              const mrpFormatted = response.data.mrp_formatted || `Rs.${labelData.mrp || 0}`;
              const usns = response.data.assigned_usns || [];
              const pairNumbers = response.data.pair_numbers || [];
              
              // Store pair batch info for combo printing
              if (isComboTemplate && response.data.pair_batch_id) {
                newPairBatchIds.push({
                  barcode: item.barcode,
                  pair_batch_id: response.data.pair_batch_id,
                  quantity: item.quantity,
                  pair_numbers: pairNumbers
                });
              }
              
              for (let usnIdx = 0; usnIdx < usns.length; usnIdx++) {
                const usn = usns[usnIdx];
                const pairNum = pairNumbers[usnIdx] || (globalPairIndex + usnIdx + 1);
                const usnWithPair = isComboTemplate ? `${usn} (#${pairNum})` : null;
                for (let copy = 0; copy < copiesPerLabel; copy++) {
                  allCommands += generateTsplFromLabelData(labelData, template, usn, mrpFormatted, usnWithPair, printerConfig, templateType);
                }
              }
              globalPairIndex += usns.length;
              totalGenerated += response.data.quantity;
            }
            
            // For combo templates, store the pair batch IDs (step change happens after successful print)
            if (isComboTemplate && newPairBatchIds.length > 0) {
              setBatchComboPairIds(newPairBatchIds);
              // Don't change step here - it will be changed after successful USB print
            }
          }
          
          commandsToSend = allCommands;
          setGeneratedCommands(allCommands);
          
          // If this was combo INNER step, print them first, then prepare for BOX
          if (isComboTemplate && currentStep === 'inner') {
            // Don't return early - actually print the inner stickers
            // After USB printing succeeds, the post-print logic will set step to 'box'
            // The message below will be shown after successful print
          }
          
          // Clear combo state after box printing (this happens AFTER successful USB print)
          // Note: This is set BEFORE the actual print, but will only execute if print succeeds
          // The actual state clearing happens in the post-print success handler
          if (isComboTemplate && currentStep === 'box') {
            // Mark that we're printing box stickers - state will be cleared after successful print
          }
          
        } catch (error) {
          const msg = error.response?.data?.detail || error.message || 'Failed to generate commands';
          console.error('[USB Print] Batch error:', error);
          toast.error(msg);
          setIsPrinting(false);
          return;
        }
      } else {
        // Single item mode - generate and print directly
        if (selectedItems.length === 0 || !selectedPricingType || !selectedTemplate) {
          toast.error('Please complete all selections first');
          return;
        }
        
        setIsPrinting(true);
        try {
          const response = await apiClient.post('/main-labels/print', {
            barcode: selectedItems[0],
            pricing_type: selectedPricingType,
            template_id: selectedTemplate,
            quantity: quantity * copiesPerLabel
          });

          // Generate TSPL commands for TSC printers (.prn format)
          const labelData = response.data.label_data || {};
          const template = response.data.template;
          const mrpFormatted = response.data.mrp_formatted || `Rs.${labelData.mrp || 0}`;
          const usns = response.data.assigned_usns || [];
          
          commandsToSend = '';
          for (const usn of usns) {
            for (let copy = 0; copy < copiesPerLabel; copy++) {
              commandsToSend += generateTsplFromLabelData(labelData, template, usn, mrpFormatted, null, printerConfig, selectedTemplateType);
            }
          }
          
          setGeneratedCommands(commandsToSend);
          fetchAvailableUsns();
        } catch (error) {
          const msg = error.response?.data?.detail || 'Failed to generate commands';
          toast.error(msg);
          setIsPrinting(false);
          return;
        }
      }
    }
    
    // Send to USB printer
    await sendToUsb(commandsToSend, printerConfig);
  };

  // Helper function to send commands to USB printer
  const sendToUsb = async (commandsToSend, printerConfig) => {
    if (!commandsToSend) {
      toast.error('No commands to print');
      setIsPrinting(false);
      return;
    }
    
    try {
      // Request USB device access
      const device = await navigator.usb.requestDevice({
        filters: [
          { vendorId: printerConfig.vendorId }, // TSC
          { vendorId: 0x0A5F }, // Zebra (fallback)
        ]
      });
      
      await device.open();
      
      // Select configuration
      if (device.configuration === null) {
        await device.selectConfiguration(1);
      }
      
      // Find the correct interface and endpoint
      let interfaceNumber = 0;
      let endpointNumber = 1;
      
      // Look for the printer interface (usually class 7 for printers)
      for (const iface of device.configuration.interfaces) {
        for (const alt of iface.alternates) {
          // Look for bulk OUT endpoint
          for (const endpoint of alt.endpoints) {
            if (endpoint.direction === 'out' && endpoint.type === 'bulk') {
              interfaceNumber = iface.interfaceNumber;
              endpointNumber = endpoint.endpointNumber;
              break;
            }
          }
        }
      }
      
      await device.claimInterface(interfaceNumber);
      
      // Convert string to bytes
      const encoder = new TextEncoder();
      const data = encoder.encode(commandsToSend);
      
      // Send data in chunks if large (USB has packet size limits)
      const chunkSize = 64; // Standard USB bulk transfer size
      for (let i = 0; i < data.length; i += chunkSize) {
        const chunk = data.slice(i, Math.min(i + chunkSize, data.length));
        await device.transferOut(endpointNumber, chunk);
      }
      
      // Release interface and close
      await device.releaseInterface(interfaceNumber);
      await device.close();
      
      toast.success('Labels sent to printer successfully!');
      setIsPrinting(false);
      
      // For combo templates, only clear batch after BOX stickers are printed
      const isComboTemplate = selectedTemplateType?.includes('combo');
      const printedStep = printingStepRef.current; // What step did we actually print?
      
      console.log(`[USB Print] Post-print: combo=${isComboTemplate}, printedStep=${printedStep}, batchItems=${batchItems.length}`);
      
      if (batchItems.length > 0 && isComboTemplate) {
        if (printedStep === 'inner') {
          // Just printed inner stickers - keep batch and dialog open, switch to box step
          setBatchComboStep('box');
          setGeneratedCommands(''); // Clear commands so user can generate box commands
          toast.info('Inner stickers printed! Now select BOX sticker template and click "Print via USB" again.');
          // Don't close dialog - user needs to print box stickers
          return;
        } else if (printedStep === 'box') {
          // Combo box stickers are done - clear everything and close
          setBatchItems([]);
          setBatchComboPairIds([]);
          setBatchComboStep('inner');
          printingStepRef.current = 'inner';
          setShowPrintDialog(false);
          toast.success('Combo printing complete! Both inner and box stickers printed.');
          fetchPendingPairBatches();
          return;
        }
      }
      
      // Non-combo or no batch items - just close
      if (batchItems.length > 0) {
        setBatchItems([]);
        setBatchComboPairIds([]);
        setBatchComboStep('inner');
      }
      setShowPrintDialog(false);
      
    } catch (error) {
      setIsPrinting(false);
      console.error('USB Print Error:', error);
      
      if (error.name === 'NotFoundError') {
        toast.error('No compatible printer found. Please connect a TSC or Zebra printer.');
      } else if (error.name === 'SecurityError') {
        toast.error('USB access denied. Please allow USB access in browser settings.');
      } else if (error.name === 'NetworkError') {
        toast.error('USB transfer failed. Try unplugging and reconnecting the printer.');
      } else {
        toast.error(`Print failed: ${error.message}`);
      }
    }
  };

  // Download function removed to prevent duplicate USN printing

  const itemOptions = items.map(item => ({
    ...item,
    displayLabel: `${item.description} (${item.barcode})`
  }));

  const canPreview = selectedItems.length > 0 && selectedPricingType && selectedTemplate;
  const canAddToBatch = canPreview && quantity > 0;
  const totalLabelsToAdd = selectedItems.length * quantity;
  const canPrint = canPreview && quantity > 0 && totalLabelsToAdd <= availableUsns;
  
  // selectedTemplateData and selectedTemplateType are defined earlier with useMemo
  const labelWidthMm = selectedTemplateData ? Math.round(selectedTemplateData.label_width_dots / 8) : 100;
  const labelHeightMm = selectedTemplateData ? Math.round(selectedTemplateData.label_height_dots / 8) : 80;

  // Global keyboard shortcuts (placed after all dependencies are defined)
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Don't trigger shortcuts when typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Alt + A = Add to List
      if (e.altKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        if (canAddToBatch) {
          handleAddToBatch();
        }
        return;
      }

      // Alt + P = Preview
      if (e.altKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        if (canPreview && !isPreviewLoading) {
          handlePreview();
        }
        return;
      }

      // Alt + Enter or Alt + R = Print All (when batch has items)
      if (e.altKey && (e.key === 'Enter' || e.key.toLowerCase() === 'r')) {
        e.preventDefault();
        if (batchItems.length > 0 && (totalBatchLabels <= availableUsns || batchComboStep !== 'inner')) {
          openPrintDialog();
        }
        return;
      }

      // Alt + C = Clear All
      if (e.altKey && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        if (batchItems.length > 0) {
          handleClearBatch();
          setBatchComboPairIds([]);
          setBatchComboStep('inner');
        }
        return;
      }

      // Alt + D = Focus Design Name dropdown
      if (e.altKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        if (designDropdownRef.current) {
          designDropdownRef.current.focus();
        }
        return;
      }

      // Escape = Close print dialog
      if (e.key === 'Escape' && showPrintDialog) {
        e.preventDefault();
        setShowPrintDialog(false);
        return;
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [canAddToBatch, canPreview, isPreviewLoading, batchItems.length, totalBatchLabels, availableUsns, batchComboStep, showPrintDialog]);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/goods-transfer')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Main Label Printing</h1>
              <p className="text-sm text-[#9CA3AF]">Generate product labels with QR codes</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 text-xs text-gray-400 border border-gray-600 rounded px-2 py-1">
              <span className="font-medium">Shortcuts:</span>
              <span>Alt+D (Design)</span>
              <span>Alt+A (Add)</span>
              <span>Alt+P (Preview)</span>
              <span>Alt+R (Print)</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <QrCode className="w-4 h-4 text-green-400" />
              <span className="text-green-400 font-semibold">{availableUsns}</span>
              <span className="text-gray-400">USNs available</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Step 1: Select Product */}
        <Card className="bg-white border-2 border-blue-200 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <Package className="w-5 h-5 text-blue-500" />
              Select Product
            </CardTitle>
            <CardDescription>
              <span className="flex items-center gap-1">
                <Filter className="w-3 h-3" />
                Filter by Category → Design → Shade → Size → Description
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Category Filter */}
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <SearchableDropdown
                label="Category 4 (Filter First)"
                placeholder="Select category to filter items..."
                options={categories}
                value={selectedCategory}
                onChange={setSelectedCategory}
                disabled={false}
                isLoading={isCategoriesLoading}
                testId="category4-dropdown"
              />
              {selectedCategory && (
                <p className="mt-2 text-xs text-yellow-700">
                  Filtering by: <strong>{selectedCategory}</strong>
                  <button 
                    onClick={() => {
                      setSelectedCategory('');
                      resetDownstream('category');
                    }}
                    className="ml-2 text-red-600 hover:underline"
                  >
                    Clear
                  </button>
                </p>
              )}
            </div>

            {/* Cascading dropdowns */}
            <div className="grid grid-cols-2 gap-4">
              <SearchableDropdown
                label="Design Name (Alt+D)"
                placeholder={selectedCategory ? "Search & select design..." : "Select category first"}
                options={designs}
                value={selectedDesign}
                onChange={setSelectedDesign}
                disabled={!selectedCategory}
                isLoading={isDesignsLoading}
                testId="design-dropdown"
                dropdownRef={designDropdownRef}
              />

              <MultiSelectDropdown
                label="Shade (Multi-Select)"
                placeholder={selectedDesign ? "Select shades..." : "Select design first"}
                options={shades}
                selectedValues={selectedShades}
                onChange={setSelectedShades}
                disabled={!selectedDesign}
                isLoading={isShadesLoading}
                testId="shade-dropdown"
              />

              <MultiSelectDropdown
                label="Size (Multi-Select)"
                placeholder={selectedShades.length > 0 ? "Select sizes..." : "Select shade first"}
                options={sizes}
                selectedValues={selectedSizes}
                onChange={setSelectedSizes}
                disabled={selectedShades.length === 0}
                isLoading={isSizesLoading}
                testId="size-dropdown"
              />

              <MultiSelectDropdown
                label="Description / Barcode (Multi-Select)"
                placeholder={selectedSizes.length > 0 ? "Select items..." : "Select size first"}
                options={itemOptions}
                selectedValues={selectedItems}
                onChange={setSelectedItems}
                disabled={selectedSizes.length === 0 || items.length === 0}
                isLoading={isItemsLoading}
                displayKey="displayLabel"
                valueKey="barcode"
                testId="item-dropdown"
              />
            </div>

            {/* Selected Items Summary */}
            {selectedItems.length > 0 && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-blue-800">
                  Selected Items: {selectedItems.length} barcode(s)
                </p>
                <div className="mt-2 max-h-32 overflow-y-auto">
                  {selectedItems.map((barcode, idx) => {
                    const itemData = items.find(i => i.barcode === barcode);
                    return (
                      <div key={barcode} className="text-xs text-gray-600 py-0.5">
                        {idx + 1}. {itemData?.design_name} | {itemData?.shade} | {itemData?.size} ({barcode})
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Pricing & Template */}
        {selectedItems.length > 0 && (
          <Card className="bg-white border-2 border-green-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <Tag className="w-5 h-5 text-green-500" />
                Pricing & Template
              </CardTitle>
              <CardDescription>
                Applies to all {selectedItems.length} selected item(s)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Pricing Type */}
                <div className="space-y-2">
                  <Label>Pricing Type</Label>
                  <Select 
                    value={selectedPricingType} 
                    onValueChange={setSelectedPricingType}
                    disabled={isPricingLoading || pricingTypes.length === 0}
                  >
                    <SelectTrigger data-testid="pricing-type-select">
                      <SelectValue placeholder={isPricingLoading ? "Loading..." : pricingTypes.length === 0 ? "No pricing found" : "Select pricing type"} />
                    </SelectTrigger>
                    <SelectContent>
                      {pricingTypes.map((pt) => (
                        <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {pricingTypes.length === 0 && !isPricingLoading && (
                    <p className="text-xs text-red-500">No pricing found. Import Rates Master first.</p>
                  )}
                </div>

                {/* Label Template */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Label Template
                    <span className="text-red-500" title="Required">*</span>
                  </Label>
                  <Select 
                    value={selectedTemplate} 
                    onValueChange={setSelectedTemplate}
                    disabled={isTemplatesLoading}
                  >
                    <SelectTrigger
                      data-testid="template-select"
                      className={!selectedTemplate
                        ? 'border-2 border-amber-400 bg-amber-50 ring-1 ring-amber-200'
                        : 'border-emerald-400'
                      }
                    >
                      <SelectValue placeholder={isTemplatesLoading
                        ? 'Loading…'
                        : 'Select template (required — verify roll size)'
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((tpl) => (
                        <SelectItem key={tpl.template_id} value={tpl.template_id}>
                          {tpl.template_name}
                          {tpl.is_default ? '  (default)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!selectedTemplate && !isTemplatesLoading && (
                    <p className="text-xs text-amber-700 flex items-start gap-1" data-testid="template-required-hint">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>Pick the template matching the label roll currently loaded in the printer. The default is no longer auto-selected to prevent mis-aligned prints.</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Pair Printing Mode - For Combo Labels */}
              {pairPrintingMode && (
                <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-amber-600" />
                    <h4 className="font-semibold text-amber-800">Combo Label Printing (Inner + Box)</h4>
                  </div>
                  
                  {/* Step indicator */}
                  <div className="flex items-center gap-2 text-sm">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded ${labelType === 'product' ? 'bg-amber-200 text-amber-800 font-semibold' : 'bg-gray-100 text-gray-500'}`}>
                      <span className="w-5 h-5 rounded-full bg-amber-600 text-white text-xs flex items-center justify-center">1</span>
                      Inner Sticker
                    </div>
                    <span className="text-gray-400">→</span>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded ${labelType === 'box' ? 'bg-green-200 text-green-800 font-semibold' : 'bg-gray-100 text-gray-500'}`}>
                      <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center ${labelType === 'box' ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>2</span>
                      Box Sticker
                    </div>
                  </div>
                  
                  <p className="text-sm text-amber-700">
                    {labelType === 'product' 
                      ? '➊ Print INNER stickers first. After printing, it will auto-switch to Box mode with same USNs.'
                      : '➋ Now print BOX stickers. These will have the SAME USNs as the inner stickers.'}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Current Step</Label>
                      <Select value={labelType} onValueChange={(val) => {
                        setLabelType(val);
                        if (val === 'product') setSelectedPairBatch(null);
                      }}>
                        <SelectTrigger className={labelType === 'box' ? 'border-green-400 bg-green-50' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="product">Step 1: Inner Sticker</SelectItem>
                          <SelectItem value="box">Step 2: Box Sticker</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {labelType === 'box' && (
                      <div className="space-y-2">
                        <Label>USN Batch (auto-selected)</Label>
                        <Select 
                          value={selectedPairBatch?.pair_batch_id || ''} 
                          onValueChange={(id) => setSelectedPairBatch(pendingPairBatches.find(b => b.pair_batch_id === id))}
                        >
                          <SelectTrigger className={selectedPairBatch ? 'border-green-400 bg-green-50' : 'border-red-300'}>
                            <SelectValue placeholder={pendingPairBatches.length === 0 ? "No pending batches" : "Select batch"} />
                          </SelectTrigger>
                          <SelectContent>
                            {pendingPairBatches.map((batch) => (
                              <SelectItem key={batch.pair_batch_id} value={batch.pair_batch_id}>
                                #{batch.starting_pair_number}-#{batch.last_pair_number} ({batch.quantity} labels)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {pendingPairBatches.length === 0 && (
                          <p className="text-xs text-red-600">⚠ Print inner stickers first to create a USN batch</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {labelType === 'product' && (
                    <p className="text-xs text-amber-600">
                      💡 After printing inner stickers, the system will automatically prepare the box sticker print with matching USNs.
                    </p>
                  )}
                  
                  {labelType === 'box' && selectedPairBatch && (
                    <div className="p-3 bg-green-50 rounded border border-green-300">
                      <p className="text-sm font-medium text-green-800">
                        ✓ Ready to print {selectedPairBatch.quantity} BOX stickers
                      </p>
                      <p className="text-sm text-green-700">
                        USNs #{selectedPairBatch.starting_pair_number} to #{selectedPairBatch.last_pair_number} (same as inner stickers)
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Quantity and Add to List */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Labels per Item</Label>
                  <Input
                    type="number"
                    min={1}
                    max={availableUsns}
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    className="text-lg"
                    data-testid="quantity-input"
                  />
                  {selectedItems.length > 0 && (
                    <p className="text-xs text-gray-500">
                      Total: {totalLabelsToAdd} labels ({selectedItems.length} items × {quantity})
                    </p>
                  )}
                  {totalLabelsToAdd > availableUsns && (
                    <p className="text-xs text-red-500">Exceeds available USNs ({availableUsns})</p>
                  )}
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handlePreview}
                    disabled={!canPreview || isPreviewLoading}
                    variant="outline"
                    className="w-full"
                    data-testid="preview-btn"
                    title="Alt+P"
                  >
                    {isPreviewLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-2" />Preview <span className="text-xs text-gray-400 ml-1">(Alt+P)</span></>
                    )}
                  </Button>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleAddToBatch}
                    disabled={!canAddToBatch}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="add-to-list-btn"
                    title="Alt+A"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add {selectedItems.length > 1 ? `${selectedItems.length} Items` : 'to List'} <span className="text-xs text-green-200 ml-1">(Alt+A)</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Batch List - Items to Print */}
        <Card className="bg-white border-2 border-orange-200 shadow-md">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <List className="w-5 h-5 text-orange-500" />
                Items to Print ({batchItems.length} items, {totalBatchLabels} labels)
              </CardTitle>
              <div className="flex items-center gap-2">
                <input
                  ref={csvFileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleImportCsvFile}
                  data-testid="csv-import-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleImportCsvClick}
                  disabled={csvImporting}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="import-csv-btn"
                  title="Upload a CSV with a `barcode` column and optional `quantity` column (sticker count per row, defaults to 1 if omitted)."
                >
                  {csvImporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  Import CSV
                </Button>
                {batchItems.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleClearBatch();
                      // Also reset combo state
                      setBatchComboPairIds([]);
                      setBatchComboStep('inner');
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Alt+C"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />Clear All <span className="text-xs text-red-400 ml-1">(Alt+C)</span>
                  </Button>
                )}
              </div>
            </div>
              
              {/* Combo Step Indicator - Show when combo template selected and batch has items */}
              {selectedTemplateType?.includes('combo') && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-amber-600" />
                    <span className="font-semibold text-amber-800 text-sm">Combo Batch Printing</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded ${batchComboStep === 'inner' ? 'bg-amber-200 text-amber-800 font-semibold' : 'bg-green-200 text-green-800'}`}>
                      <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center ${batchComboStep === 'inner' ? 'bg-amber-600 text-white' : 'bg-green-600 text-white'}`}>
                        {batchComboStep === 'inner' ? '1' : '✓'}
                      </span>
                      Inner Stickers
                    </div>
                    <span className="text-gray-400">→</span>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded ${batchComboStep === 'box' ? 'bg-green-200 text-green-800 font-semibold' : 'bg-gray-100 text-gray-500'}`}>
                      <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center ${batchComboStep === 'box' ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>2</span>
                      Box Stickers
                    </div>
                  </div>
                  {batchComboStep === 'inner' && (
                    <p className="text-xs text-amber-700 mt-2">
                      ➊ Click "Print All" to generate INNER sticker commands first
                    </p>
                  )}
                  {batchComboStep === 'box' && (
                    <p className="text-xs text-green-700 mt-2">
                      ➋ Inner stickers ready! Click "Generate Commands" again to get BOX stickers with same USNs
                    </p>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {batchItems.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6" data-testid="batch-empty-hint">
                  Print list is empty. Add items via the form above or use <span className="font-semibold text-blue-700">Import CSV</span> for bulk barcode upload.
                </p>
              ) : (
              <>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {batchItems.map((item, idx) => (
                  <div 
                    key={item.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{item.designName}</p>
                        <p className="text-xs text-gray-500">
                          {item.shade} | {item.size} | {item.pricingType}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
                        {item.quantity} labels
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemoveFromBatch(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Print All Button */}
              <div className="mt-4 pt-4 border-t">
                {/* Check if any item has box_template - show two buttons for combo items */}
                {batchItems.some(item => item.boxTemplate) ? (
                  <div className="space-y-3">
                    <p className="text-xs text-blue-600 text-center font-medium">
                      📦 Combo items detected - Print stickers separately
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        onClick={() => {
                          setBatchComboStep('inner');
                          openPrintDialog();
                        }}
                        disabled={totalBatchLabels > availableUsns}
                        className="h-12 text-sm bg-amber-600 hover:bg-amber-700"
                        data-testid="print-product-sticker-btn"
                      >
                        <Tag className="w-4 h-4 mr-2" />
                        Product Sticker ({totalBatchLabels})
                      </Button>
                      <Button 
                        onClick={() => {
                          setBatchComboStep('box');
                          openPrintDialog();
                        }}
                        disabled={batchComboPairIds.length === 0}
                        className="h-12 text-sm bg-green-600 hover:bg-green-700"
                        data-testid="print-box-sticker-btn"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Box Sticker ({batchComboPairIds.reduce((sum, p) => sum + p.quantity, 0) || totalBatchLabels})
                      </Button>
                    </div>
                    {batchComboPairIds.length === 0 && (
                      <p className="text-xs text-amber-600 text-center">
                        ⚠️ Print Product Stickers first, then Box Stickers will be enabled
                      </p>
                    )}
                    {batchComboPairIds.length > 0 && (
                      <p className="text-xs text-green-600 text-center">
                        ✓ Product stickers printed. Ready for Box Stickers with same USNs
                      </p>
                    )}
                  </div>
                ) : (
                  <Button 
                    onClick={openPrintDialog}
                    disabled={totalBatchLabels > availableUsns && batchComboStep === 'inner'}
                    className={`w-full h-12 text-lg ${batchComboStep === 'box' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}
                    data-testid="print-all-btn"
                    title="Alt+Enter or Alt+R"
                  >
                    <Printer className="w-5 h-5 mr-2" />
                    {selectedTemplateType?.includes('combo') 
                      ? (batchComboStep === 'inner' 
                          ? `Print INNER Stickers (${totalBatchLabels} Labels)` 
                          : `Print BOX Stickers (${batchComboPairIds.reduce((sum, p) => sum + p.quantity, 0)} Labels)`)
                      : `Print All (${totalBatchLabels} Labels)`
                    }
                    <span className="text-xs opacity-70 ml-2">(Alt+R)</span>
                  </Button>
                )}
                {totalBatchLabels > availableUsns && batchComboStep === 'inner' && (
                  <p className="text-xs text-red-500 mt-2 text-center">
                    Total labels ({totalBatchLabels}) exceeds available USNs ({availableUsns})
                  </p>
                )}
              </div>
              </>
              )}
            </CardContent>
          </Card>

        {/* Step 3: Preview & Print */}
        {previewData && (
          <Card className="bg-white border-2 border-purple-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <Printer className="w-5 h-5 text-purple-500" />
                Label Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Label Preview - Clean design without separators */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-white mb-4">
                <div className="max-w-md mx-auto space-y-2 font-mono text-sm">
                  {/* Quality */}
                  <div className="flex gap-4">
                    <span className="text-gray-500 w-20">QUALITY</span>
                    <span className="font-semibold">{previewData.label_data?.quality || '-'}</span>
                  </div>
                  
                  {/* Content - Display with spaces, no "&" separator */}
                  {previewData.label_data?.content1 && (
                    <div className="flex gap-4">
                      <span className="text-gray-500 w-20">CONTENT</span>
                      <div className="flex gap-6">
                        <span className="font-semibold">{previewData.label_data.content1}</span>
                        {previewData.label_data.content2 && (
                          <span className="font-semibold">{previewData.label_data.content2}</span>
                        )}
                        {previewData.label_data.content3 && (
                          <span className="font-semibold">{previewData.label_data.content3}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Size - Display from Desc2, Desc4, Desc7 with spaces */}
                  <div className="flex gap-4">
                    <span className="text-gray-500 w-20">SIZE</span>
                    <div className="flex gap-6">
                      {selectedTemplateType === 'single_dhoti' ? (
                        <span>{previewData.label_data?.size_from_rates || '-'}</span>
                      ) : (
                        <>
                          {previewData.label_data?.size1 && <span>{previewData.label_data.size1}</span>}
                          {previewData.label_data?.size2 && <span>{previewData.label_data.size2}</span>}
                          {previewData.label_data?.size3 && <span>{previewData.label_data.size3}</span>}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Color (not for dhoti) */}
                  {previewData.label_data?.color && selectedTemplateType !== 'single_dhoti' && (
                    <div className="flex gap-4">
                      <span className="text-gray-500 w-20">COLOR</span>
                      <span>{previewData.label_data.color}</span>
                    </div>
                  )}

                  {/* Bold Description 5 (for combos) */}
                  {previewData.label_data?.desc5_bold && selectedTemplateType?.includes('combo') && (
                    <div className="text-right">
                      <span className="text-2xl font-bold text-purple-600">
                        {previewData.label_data.desc5_bold}
                      </span>
                    </div>
                  )}

                  {/* QTY and MRP - with MRP label */}
                  <div className="flex justify-between items-center pt-3 mt-3 border-t border-gray-200">
                    <span className="font-semibold">{previewData.label_data?.qty}</span>
                    <div className="text-right">
                      <span className="text-gray-500 text-xs mr-1">MRP:</span>
                      <span className="text-xl font-bold">{previewData.mrp_formatted}</span>
                      <div className="text-xs text-gray-500">(INCL. OF ALL TAXES)</div>
                    </div>
                  </div>

                  {/* Made in India */}
                  <div className="text-center text-gray-600 font-semibold pt-2">
                    MADE IN INDIA
                  </div>

                  {/* QR Codes */}
                  <div className="flex justify-between items-center pt-3 mt-2 border-t border-gray-200">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center mb-1">
                        <QrCode className="w-10 h-10 text-gray-500" />
                      </div>
                      <span className="text-xs">{previewData.preview_usn}</span>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center mb-1">
                        <QrCode className="w-10 h-10 text-gray-500" />
                      </div>
                      <span className="text-xs">{previewData.label_data?.qr2_code || '-'}</span>
                    </div>
                  </div>

                  {/* Consumer Notice */}
                  <div className="p-2 border border-gray-300 rounded text-xs text-center text-gray-600 mt-2">
                    Consumer is free to open and inspect the product before buying it
                  </div>
                </div>
              </div>

              {/* Print Button - Opens Dialog */}
              <Button 
                onClick={openPrintDialog}
                disabled={!canPrint}
                className="w-full bg-[#2D5F4F] hover:bg-[#234A3D] h-12 text-lg"
                data-testid="print-btn"
              >
                <Printer className="w-5 h-5 mr-2" />
                Print {quantity} Label{quantity > 1 ? 's' : ''}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Print Dialog - Similar to user's image */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Print Product Labels
            </DialogTitle>
            <p className="text-sm text-gray-500">
              {batchItems.length > 0 
                ? `${totalBatchLabels} labels to print (${batchItems.length} items in batch)` 
                : `${quantity} labels to print • ${selectedTemplateData?.template_name} (${labelWidthMm}mm x ${labelHeightMm}mm)`}
            </p>
          </DialogHeader>

          <Tabs value={printDialogTab} onValueChange={setPrintDialogTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="settings" className="flex items-center gap-1">
                <Settings className="w-4 h-4" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="preview" className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                Preview
              </TabsTrigger>
              <TabsTrigger value="commands" disabled={!generatedCommands}>
                Commands
              </TabsTrigger>
            </TabsList>

            <TabsContent value="settings" className="space-y-4 pt-4">
              {/* 2026-05-13 — Template Confirmation Banner.
                  User reported misalignment when the auto-selected default
                  template's roll size did not match the physical label roll.
                  This banner shows the active template's dimensions in mm
                  and lets the operator re-select right before printing. */}
              <div
                className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50 space-y-2"
                data-testid="confirm-template-banner"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-700" />
                  <span className="font-semibold text-amber-800">
                    Confirm Label Template
                  </span>
                </div>
                <p className="text-xs text-amber-800">
                  Verify the template matches the physical label roll loaded
                  in the printer. The previous default-template auto-selection
                  has been removed because mismatched roll sizes were causing
                  printed stickers to mis-align.
                </p>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger
                    data-testid="confirm-template-select"
                    className={!selectedTemplate ? 'border-2 border-red-400 bg-white' : 'border-emerald-400 bg-white'}
                  >
                    <SelectValue placeholder="Select template (required)" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((tpl) => (
                      <SelectItem key={tpl.template_id} value={tpl.template_id}>
                        {tpl.template_name}
                        {' '}({Math.round(tpl.label_width_dots / 8)}mm × {Math.round(tpl.label_height_dots / 8)}mm)
                        {tpl.is_default ? '  — default' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTemplateData && (
                  <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                    Selected: <span className="font-semibold">{selectedTemplateData.template_name}</span>
                    {' '}• {Math.round((selectedTemplateData.label_width_dots || 0) / 8)}mm × {Math.round((selectedTemplateData.label_height_dots || 0) / 8)}mm
                    {selectedTemplateData.template_type ? ` • ${selectedTemplateData.template_type}` : ''}
                  </div>
                )}
              </div>

              {/* Template Selection for Combo Mode - Prominent at top */}
              {batchItems.length > 0 && selectedTemplateType?.includes('combo') && (
                <div className={`p-4 rounded-lg border-2 ${batchComboStep === 'inner' ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-5 h-5" />
                    <span className="font-semibold">
                      {batchComboStep === 'inner' ? 'Select INNER Sticker Template' : 'Select BOX Sticker Template'}
                    </span>
                  </div>
                  <Select value={dialogTemplate} onValueChange={setDialogTemplate}>
                    <SelectTrigger className={`${batchComboStep === 'inner' ? 'border-amber-400' : 'border-green-400'}`}>
                      <SelectValue placeholder="Select template for this step" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((tpl) => (
                        <SelectItem key={tpl.template_id} value={tpl.template_id}>
                          {tpl.template_name} ({Math.round(tpl.label_width_dots / 8)}mm x {Math.round(tpl.label_height_dots / 8)}mm)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs mt-2 text-gray-600">
                    {batchComboStep === 'inner' 
                      ? 'Choose the template size for inner/product stickers' 
                      : 'Choose the template size for box stickers (can be different from inner)'}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Printer Model</Label>
                  <Select value={printerModel} onValueChange={setPrinterModel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TSC_TE210">TSC TE210 (Desktop)</SelectItem>
                      <SelectItem value="TSC_MH240">TSC MH240 (Industrial)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Printer Language</Label>
                  <Select value={printerLanguage} onValueChange={setPrinterLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TSPL">TSPL (TSC Printers)</SelectItem>
                      <SelectItem value="ZPL">ZPL (Zebra Printers)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Font</Label>
                  <div className="p-2 bg-amber-50 rounded border border-amber-200 text-sm">
                    <span className="font-medium text-amber-800">Arial Bold (ARIALB.TTF)</span>
                    <p className="text-xs text-amber-600 mt-1">
                      Sizes configured per-field in Admin → Main Label Templates
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Copies per Label</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={copiesPerLabel}
                    onChange={(e) => setCopiesPerLabel(parseInt(e.target.value) || 1)}
                  />
                </div>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p className="font-medium text-blue-700">{PRINTER_CONFIGS[printerModel]?.name}</p>
                <p className="text-blue-600 text-xs">
                  DPI: {PRINTER_CONFIGS[printerModel]?.dpi} | Speed: {PRINTER_CONFIGS[printerModel]?.speed}
                </p>
              </div>

              {/* Label Information */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <h4 className="font-medium text-gray-700">Label Information</h4>
                {batchItems.length > 0 ? (
                  // Batch Mode Info
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Mode:</span>
                        <span className="ml-1 font-medium text-orange-600">Batch Printing</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Items:</span>
                        <span className="ml-1 font-medium">{batchItems.length}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Total Labels:</span>
                        <span className="ml-1 font-medium">{totalBatchLabels * copiesPerLabel}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Copies/Label:</span>
                        <span className="ml-1 font-medium">{copiesPerLabel}</span>
                      </div>
                    </div>
                    <div className="mt-2 max-h-24 overflow-y-auto text-xs bg-white p-2 rounded border">
                      {batchItems.map((item, idx) => (
                        <div key={item.id} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                          <span>{idx + 1}. {item.designName}</span>
                          <span className="text-orange-600">{item.quantity} labels</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Single Mode Info
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <span className="ml-1 font-medium">{selectedTemplateData?.template_name?.split(' - ')[0] || 'Product'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Size:</span>
                      <span className="ml-1 font-medium">{labelWidthMm}mm x {labelHeightMm}mm</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Total Labels:</span>
                      <span className="ml-1 font-medium">{quantity * copiesPerLabel}</span>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="preview" className="pt-4">
              <div className="border rounded-lg p-4 bg-white max-h-64 overflow-y-auto">
                <div className="space-y-1 font-mono text-xs">
                  <div><span className="text-gray-500">QUALITY:</span> {previewData?.label_data?.quality}</div>
                  {previewData?.label_data?.content1 && (
                    <div>
                      <span className="text-gray-500">CONTENT:</span>{' '}
                      {[previewData.label_data.content1, previewData.label_data.content2, previewData.label_data.content3].filter(Boolean).join('    ')}
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">SIZE:</span>{' '}
                    {[previewData?.label_data?.size1, previewData?.label_data?.size2, previewData?.label_data?.size3].filter(Boolean).join('    ') || previewData?.label_data?.size_from_rates || '-'}
                  </div>
                  <div><span className="text-gray-500">MRP:</span> {previewData?.mrp_formatted}</div>
                  <div><span className="text-gray-500">QR1:</span> {previewData?.preview_usn}</div>
                  <div><span className="text-gray-500">QR2:</span> {previewData?.label_data?.qr2_code}</div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="commands" className="pt-4">
              <div className="border rounded-lg p-4 bg-gray-900 text-green-400 font-mono text-xs max-h-64 overflow-y-auto">
                <pre className="whitespace-pre-wrap">{generatedCommands || 'No commands generated yet'}</pre>
              </div>
            </TabsContent>
          </Tabs>

          {/* Print Method Section */}
          <div className="space-y-3 pt-4 border-t">
            <Label className="text-gray-700">Print Method</Label>
            
            <Button 
              onClick={handlePrintViaUsb}
              disabled={isPrinting || !selectedTemplate}
              className="w-full bg-[#2D5F4F] hover:bg-[#234A3D] h-11 disabled:bg-gray-400"
              data-testid="print-usb-btn"
              title={!selectedTemplate ? 'Select a template above to enable printing' : ''}
            >
              {isPrinting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
              ) : !selectedTemplate ? (
                <><AlertTriangle className="w-4 h-4 mr-2" />Select a template first</>
              ) : (
                <><Usb className="w-4 h-4 mr-2" />Print via USB</>
              )}
            </Button>

            {/* Generate Commands button - only for viewing/debugging, hidden by default */}
            {batchItems.length > 0 && selectedTemplateType?.includes('combo') && (
              <Button 
                onClick={generateCommands}
                disabled={isPrinting}
                variant="outline"
                className={`w-full h-11 ${batchComboStep === 'box' ? 'border-green-500 text-green-700 hover:bg-green-50' : 'border-amber-500 text-amber-700 hover:bg-amber-50'}`}
                data-testid="generate-commands-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                {batchComboStep === 'inner' ? 'Generate INNER Commands' : 'Generate BOX Commands'}
              </Button>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button variant="ghost" onClick={() => setShowPrintDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MainLabelPrinting;
